import { useEffect, useState, type HTMLAttributes } from 'react';
import { cn } from './lib/cn';

export type AvatarAvailability = 'available' | 'busy' | 'away';

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  src?: string | null;
  name: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  availability?: AvatarAvailability;
  /** @deprecated Use availability instead. */
  status?: 'online' | 'busy' | 'offline';
  loading?: boolean;
}

const sizeClasses = {
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-14 text-base',
  xl: 'size-20 text-xl',
};

const badgeClasses = {
  available: 'bg-[var(--success)]',
  busy: 'bg-[var(--warning)]',
  away: 'bg-[var(--muted-foreground)]',
};

const availabilityLabels = {
  available: 'Livre',
  busy: 'Em atendimento',
  away: 'Ausente',
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  if (first && second) {
    return `${first}${second}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function Avatar({
  src,
  name,
  size = 'md',
  availability,
  status,
  loading = false,
  className,
  ...props
}: AvatarProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const resolvedAvailability =
    availability ?? (status === 'online' ? 'available' : status === 'offline' ? 'away' : status);
  const availabilityLabel = resolvedAvailability
    ? availabilityLabels[resolvedAvailability]
    : undefined;
  const accessibleLabel =
    props['aria-label'] ?? (availabilityLabel ? `${name}, ${availabilityLabel}` : name);

  useEffect(() => setImageFailed(false), [src]);

  return (
    <div
      {...props}
      role={props.role ?? 'img'}
      aria-label={accessibleLabel}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex shrink-0 select-none items-center justify-center rounded-full font-black',
        sizeClasses[size],
        loading && 'animate-pulse motion-reduce:animate-none',
        className,
      )}
    >
      {loading ? (
        <span className="size-full rounded-full border border-[var(--border)] bg-[var(--surface-raised)]" />
      ) : src && !imageFailed ? (
        <img
          src={src}
          alt=""
          className="size-full rounded-full border border-[var(--border)] object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="flex size-full items-center justify-center rounded-full border border-[var(--border)] bg-[var(--accent-soft)] text-[var(--accent)]">
          {getInitials(name)}
        </span>
      )}
      {!loading && resolvedAvailability && (
        <span
          title={availabilityLabel}
          className={cn(
            'absolute bottom-0 right-0 size-3 rounded-full border-2 border-[var(--surface)] shadow-[0_0_0_1px_var(--border)]',
            badgeClasses[resolvedAvailability],
          )}
        >
          <span className="sr-only">{availabilityLabel}</span>
        </span>
      )}
    </div>
  );
}
